import PropTypes from 'prop-types';
import { Col, Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { injectIntl } from 'react-intl';
import { messages, prohibited } from '../views/Job/Job.messages';
import useFormatter from '../hooks/useFormatter';
import useJob from '../hooks/useJob';
import useSettings from '../hooks/useSettings';

/**
 * Payment action buttons of the invoice summary: move paydate, create payplan and pay.
 * Renders a set of grid columns, so it has to be placed directly inside a react-bootstrap Row.
 */
const InvoicePaymentActions = ({ intl, job, currency, buttonColor, buttonTextColor, onMovePaydate, onCreatePayplan, onPay }) => {
    const lng = intl.formatMessage;
    const { formatCurrency } = useFormatter();
    const { hasPaymentActions } = useJob(job);
    const { paymentlimit } = useSettings();

    if (!hasPaymentActions) {
        return null;
    }

    // Payment limit can prevent users from paying the invoice, if the open amount on the invoice exceeds it.
    // Payment limits are configured with env.variable VITE_PAYMENT_LIMIT
    const disablePayButton = paymentlimit > 0 && job?.openamount > paymentlimit;

    const paymentTermExtensionCannotBeMade = job.cid === 1046233
        ? lng(prohibited.paymentTermExtensionCannotBeMadeCustom)
        : lng(prohibited.paymentTermExtensionCannotBeMade);

    const payButtonStyle = { backgroundColor: disablePayButton ? null : buttonColor, color: buttonTextColor };
    const payButtonClassName = 'pay w-100  ' + (disablePayButton ? 'primary-disabled' : '');
    const payColumnClassName = 'justify-content-end ' + (job.payplan !== null ? ' w-100' : '');

    /**
     * Postpone paydate button.
     * If paydate cannot be postponed, button will be disabled,
     * and information will be shown to user in a tooltip.
     */
    const paydateButtonElement = (
        <OverlayTrigger
            key='new-paydate-button'
            placement='top'
            trigger={['hover', 'click']}
            overlay={<Tooltip id={'paydate-cant-be-changed'}>{paymentTermExtensionCannotBeMade}</Tooltip>}
            show={job.modpaydate ? false : undefined}
        >
            <Button id='new-paydate-button'
                aria-label={lng(messages.movePaydate)}
                className={'extend-duedate w-100 ' + (!job.modpaydate ? 'secondary-disabled' : '')}
                variant='secondary'
                size='sm'
                onClick={job.modpaydate ? onMovePaydate : undefined}>
                {lng(messages.movePaydate)} <FontAwesomeIcon icon={far.faClockRotateLeft} />
            </Button>
        </OverlayTrigger>
    );

    /**
     * Create payplan button.
     * If payplan cannot be created, button will be disabled,
     * and information will be shown to user in a tooltip.
     */
    const payplanButtonElement = (
        <OverlayTrigger
            key='show-payplan'
            trigger={['hover', 'click']}
            overlay={
                <Tooltip id='payplan-cannot-be-created'>
                    {job.cid === 1046233
                        ? lng(prohibited.paymentTermExtensionCannotBeMadeCustom)
                        : lng(prohibited.paymentplanCannotBeCreated)}
                </Tooltip>
            }
            show={job.dopayplan ? false : undefined}
        >
            <Button
                aria-label={lng(messages.payplan)}
                className={'extend-duedate ' + (!job.dopayplan ? 'secondary-disabled' : '')}
                variant='secondary'
                size='sm'
                onClick={job.dopayplan ? onCreatePayplan : undefined}>
                {lng(messages.payplan)} <FontAwesomeIcon icon={far.faCalendarClock} />
            </Button>
        </OverlayTrigger>
    );

    /**
     * Pay button, disabled with an explanatory tooltip when the payment limit is exceeded.
     */
    const payButtonElement = (
        <OverlayTrigger
            key='show-payment-limit-exceeded'
            trigger={['hover', 'click']}
            rootClose={true}
            placement='bottom'
            overlay={
                <Tooltip id='payment-limit-exceeded'>
                    {lng(prohibited.paymentLimit)} {formatCurrency(paymentlimit, currency)}
                    . {lng(prohibited.paymentLimitExceeded)}
                </Tooltip>
            }
            show={disablePayButton ? undefined : false}>
            <Button
                id='pay'
                className={payButtonClassName}
                variant="primary"
                onClick={disablePayButton ? undefined : onPay}
                style={payButtonStyle}
            >
                {lng(messages.pay)}
            </Button>
        </OverlayTrigger>
    );

    /**
     * Pay button for whitelabel preview purposes only.
     *
     * This button has no onClick handler or other functionalities,
     * and is only used to demonstrate the pay button style for the whitelabel setting preview.
     */
    const previewPayButtonElement = (
        <Button
            id='pay'
            className={payButtonClassName}
            variant="primary"
            style={payButtonStyle}
        >
            {lng(messages.pay)}
        </Button>
    );

    return (
        <>
            <Col xs={{ span: 6, order: 2 }} lg={{ span: 4, order: 1 }} xl={{ span: 4, order: 2 }} xxl={{ span: 3, order: 2 }} className='pt-3 pt-lg-0 pe-1 pe-lg-0 '>
                {job.payplan === null && paydateButtonElement}
            </Col>
            <Col xs={{ span: 6, order: 3 }} lg={{ span: 4, order: 2 }} xl={{ span: 4, order: 3 }} xxl={{ span: 3, order: 3 }} className='pt-3 pt-lg-0  ps-1 ps-lg-2 '>
                {job.payplan === null && payplanButtonElement}
            </Col>
            {/* Pay button can be hidden also for unpaid jobs when makepayment=false */}
            {job?.makepayment && (
                <Col xs={{ order: 1 }} lg={{ span: 4, order: 3 }} xl={{ span: 4, order: 4 }} xxl={{ span: 6, order: 4 }} className={payColumnClassName}>
                    {payButtonElement}
                </Col>
            )}
            {/* Preview button only for demonstration purposes */}
            {job?.preview && (
                <Col xs={{ order: 1 }} lg={{ span: 4, order: 3 }} xl={{ span: 4, order: 4 }} xxl={{ span: 6, order: 4 }} className={payColumnClassName}>
                    {previewPayButtonElement}
                </Col>
            )}
        </>
    );
};

InvoicePaymentActions.propTypes = {
    'intl': PropTypes.object.isRequired,
    'job': PropTypes.object.isRequired,
    'currency': PropTypes.string,
    'buttonColor': PropTypes.string,
    'buttonTextColor': PropTypes.string,
    'onMovePaydate': PropTypes.func.isRequired,
    'onCreatePayplan': PropTypes.func.isRequired,
    'onPay': PropTypes.func.isRequired
};

export default injectIntl(InvoicePaymentActions);
